import { generateFoodNameSlug } from 'common/FoodUtils';
import { isDefined } from 'common/TypeUtilities';
import { Slugs } from 'common/urls/Slugs';
import { chain, flatMap, isEmpty, isNil, map, size, some, sumBy, values } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { FoodItemStatus, FoodType, UpcomingOrderFieldsFragment } from 'src/gqlReactTypings.generated.d';
import { SIDE_FOOD_TYPES } from 'src/shared/Constants';
import { AvatarImageGroup } from 'src/shared/design-system/Avatar';
import { Routes } from 'src/shared/Routes';
import { getIdSluggedPath, getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { match } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, ScreenWidth } from 'web-common/src/shared/styles';
import {
  BecomeAShefIcon,
  OrdersCardBody,
  OrdersCardBodyRow,
  OrdersCardBodyText,
  OrdersCardDishImagePreview,
  UtensilBowlIcon,
} from './styles';

interface PreviewFoodItem {
  id: number;
  name: string;
  userId: string;
  status: FoodItemStatus.Approved;
  imageUrl: string;
  foodType: FoodType;
  ratingCount: number;
}

interface Props {
  orders: UpcomingOrderFieldsFragment[];
}

export const ConsumerOrdersCardBody: React.FC<Props> = ({ orders }) => {
  const { nearestScreenWidth } = useScreenContext();

  const lineItems = flatMap(orders, (order) => order.lineItems);

  const validFoodItems = chain(lineItems)
    .map('foodItem')
    .filter((item): item is PreviewFoodItem => !some(values(item), isNil) && item.status === FoodItemStatus.Approved)
    .sort(size)
    .uniqBy('id')
    .value();

  const { maxNumPreviewItems, dishImagePreviewSize } = match(nearestScreenWidth)
    .with(ScreenWidth.EXTRA_EXTRA_SMALL, ScreenWidth.EXTRA_SMALL, ScreenWidth.SMALL, () => ({
      maxNumPreviewItems: 1,
      dishImagePreviewSize: 64,
    }))
    .with(ScreenWidth.MEDIUM, () => ({ maxNumPreviewItems: 2, dishImagePreviewSize: 72 }))
    .otherwise(() => ({ maxNumPreviewItems: 3, dishImagePreviewSize: 80 }));

  // Move all sides to the end of the list
  const previewFoodItems = [...validFoodItems]
    .sort((a, b) =>
      match({ aIsSide: SIDE_FOOD_TYPES.includes(a.foodType), bIsSide: SIDE_FOOD_TYPES.includes(b.foodType) })
        .with({ aIsSide: false, bIsSide: true }, () => -1)
        .with({ aIsSide: true, bIsSide: false }, () => 1)
        .otherwise(() => 0)
    )
    .slice(0, maxNumPreviewItems);

  const numItems = sumBy(lineItems, 'quantity') - Math.min(maxNumPreviewItems, size(previewFoodItems));

  const orderDescription =
    map(previewFoodItems, 'name').join(', ') +
    match(numItems)
      .with(0, () => '')
      .with(1, () => ' + 1 Item')
      .otherwise((num) => ` + ${num} Items`);

  const { publicShef } = orders[0];
  const isMealPlanOrder = publicShef.publicName.includes('Meal Plan');
  const additionalShefsDescription = match(size(orders))
    .with(1, () => null)
    .with(2, () => ' + 1 shef')
    .otherwise((num) => ` + ${num - 1} shefs`);

  const shefImageUrls = orders
    .map((order) => order.publicShef.resizedImageUrl)
    .filter(isDefined)
    .slice(0, 3);

  return (
    <OrdersCardBody>
      {map(previewFoodItems, (item) => (
        <Link
          key={item.id}
          to={getSluggedPath(Routes.CONSUMER_ORDER_FOOD_ITEM, [
            { slug: Slugs.ID, value: item.userId },
            { slug: Slugs.NAME, value: generateFoodNameSlug(item) },
          ])}>
          <OrdersCardDishImagePreview
            alt={item.name}
            src={item.imageUrl}
            height={dishImagePreviewSize}
            width={dishImagePreviewSize}
            sourceHeight={dishImagePreviewSize}
          />
        </Link>
      ))}
      <div>
        {!isMealPlanOrder && (
          <OrdersCardBodyRow>
            <BecomeAShefIcon color={Colors.GRAY_DARK_10} />
            <OrdersCardBodyText>
              Made by{' '}
              <Link to={getIdSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, publicShef.id)}>{publicShef.publicName}</Link>
              {additionalShefsDescription}
            </OrdersCardBodyText>
            {!isEmpty(shefImageUrls) && <AvatarImageGroup size={24} imageUrls={shefImageUrls} />}
          </OrdersCardBodyRow>
        )}
        <OrdersCardBodyRow>
          <UtensilBowlIcon color={Colors.GRAY_DARK_10} />
          <OrdersCardBodyText>{orderDescription}</OrdersCardBodyText>
        </OrdersCardBodyRow>
      </div>
    </OrdersCardBody>
  );
};
